import { CognitoUserPool } from "amazon-cognito-identity-js";

/**
 * Which sign-in surface this build uses.
 *
 * <ul>
 *   <li>{@code "cognito"} — our own sign-in pages, talking to the Cognito user
 *       pool directly (SRP). Federated buttons still redirect, but through
 *       Cognito straight to the provider, so its own pages never render.</li>
 *   <li>{@code "oidc"} — the classic redirect-to-the-IdP flow via
 *       react-oidc-context. What local dev (Keycloak) uses, and the rollback
 *       for production.</li>
 * </ul>
 */
export type AuthMode = "cognito" | "oidc";

/**
 * Cognito issuer URLs look like
 * {@code https://cognito-idp.<region>.amazonaws.com/<user-pool-id>}. That single
 * value already carries everything the SDK needs, so native sign-in adds no new
 * required environment variable — it reads the pool out of the authority the SPA
 * is already configured with.
 *
 * @returns the pool coordinates, or null when the authority isn't a Cognito one
 *          (i.e. Keycloak in local dev).
 */
export function parseCognitoAuthority(
  authority: string | undefined,
): { region: string; userPoolId: string } | null {
  if (!authority) return null;
  const match = /^https:\/\/cognito-idp\.([a-z0-9-]+)\.amazonaws\.com\/([\w-]+)\/?$/.exec(
    authority.trim(),
  );
  if (!match) return null;
  return { region: match[1], userPoolId: match[2] };
}

/**
 * The active mode.
 *
 * <p>Defaults to whatever the configured issuer can support — Cognito issuer →
 * native pages, anything else → redirect flow. That default is deliberate: it
 * means production picks up the branded sign-in with no deploy-time environment
 * change (and no chance of a half-configured build), while Keycloak-backed local
 * dev keeps working untouched.</p>
 *
 * <p>{@code VITE_AUTH_MODE=oidc} forces the old behaviour — the one-variable
 * rollback if the native pages ever misbehave in production.</p>
 */
export function authMode(): AuthMode {
  const forced = import.meta.env.VITE_AUTH_MODE;
  if (forced === "oidc" || forced === "cognito") return forced;
  return parseCognitoAuthority(import.meta.env.VITE_OIDC_AUTHORITY) ? "cognito" : "oidc";
}

let cached: CognitoUserPool | null = null;

/**
 * The user pool the native pages authenticate against, or null when this build
 * isn't Cognito-backed. Cached: the SDK object holds the local-storage session
 * cache, so one instance per app is what we want.
 */
export function userPool(): CognitoUserPool | null {
  if (cached) return cached;
  const parsed = parseCognitoAuthority(import.meta.env.VITE_OIDC_AUTHORITY);
  if (!parsed) return null;
  cached = new CognitoUserPool({
    UserPoolId: parsed.userPoolId,
    ClientId: import.meta.env.VITE_OIDC_CLIENT_ID,
  });
  return cached;
}

/** Test seam: drop the memoised pool so a test can re-read the environment. */
export function resetUserPoolForTests(): void {
  cached = null;
}
